import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mur_a1, mur_a2, mur_b1, mur_b2, sol_a1, sol_a2, sol_b1, sol_b2, iconPeinture, iconLoupe, iconEtanche, iconShield } from '../images/imageImports';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useSeo from '../hooks/useSeo';
import './PeinturePage.css';

const PeinturePage = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [servicesRef, servicesVisible] = useScrollAnimation(0.1);
  const [processRef, processVisible] = useScrollAnimation(0.1);
  const [photosRef, photosVisible] = useScrollAnimation(0.1);
  useSeo({
    title: 'Peinture · Vie et couleurs (Couëron)',
    description: 'Peinture intérieure et extérieure à Couëron - Région nantaise : murs, plafonds, boiseries, sols. Finitions propres et durables par un artisan qualifié en région nantaise. Devis gratuit.',
    canonical: 'https://vie-et-couleurs.nokram.fr/peinture-interieure'
  });

  const photos = [
    {
      id: 1,
      title: "Murs et plafond",
      description: "Travaux de plâtrerie (enduit et ponçage des joints de plaques de plâtre), peinture des murs et du plafond, et mise en couleur des poutres et d'un pan de mur.",
      beforeSrc: mur_a1,
      afterSrc: mur_a2
    },
    {
      id: 2,
      title: "Murs et plafond",
      description: "Finition des joints de plâtrerie et travaux de peinture sur les murs, le plafond et la charpente apparente.",
      beforeSrc: mur_b1,
      afterSrc: mur_b2
    },
    {
      id: 3,
      title: "Sols",
      description: "Préparation du support et application d'une résine de sol (époxy ou polyuréthane) pour créer un revêtement de sol durable et uniforme.",
      beforeSrc: sol_a1,
      afterSrc: sol_a2
    },
    {
      id: 4,
      title: "Sols",
      description: "Pose d'un revêtement de sol souple (vinyle/PVC) sur carrelage existant et finition des murs par la pose de plinthes.",
      beforeSrc: sol_b1,
      afterSrc: sol_b2
    }
  ];

  const openModal = (photo) => {
    setSelectedPhoto(photo);
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  // Empêcher le scroll de l'arrière-plan quand la modale est ouverte
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Nettoyer l'effet au démontage du composant
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedPhoto]);
  return (
    <div className="peinture-page">
      {/* Header Section */}
      <section className="peinture-header">
        <div className="container">
          <div>
            <h1 className="page-title">Peinture</h1>
            <p className="page-subtitle">
              Découvrez notre savoir-faire en peinture, rénovation complète et finitions soignées
            </p>
          </div>
        </div>
      </section>

      {/* Peinture Section */}
      <section id="peinture-interieure" className="service-section">
        <div className="container">
          <div className={`service-content scroll-animate ${servicesVisible ? 'visible' : ''}`} ref={servicesRef}>
            <div className="service-text">
              <h2 className="service-title"><img src={iconPeinture} alt="Peinture" className="title-icon" />Peinture intérieure</h2>
              <p className="service-description">
                Notre savoir-faire en peinture intérieure couvre tous vos besoins, de la rénovation complète aux finitions soignées. 
                Nous utilisons des matériaux de qualité et des techniques professionnelles pour des résultats durables et esthétiques.
              </p>
              
              <div className="service-details">
                <h3>Prestations</h3>
                <ul>
                  <li>Peinture mur plafond</li>
                  <li>Sol</li>
                  <li>Pose de papier peint</li>
                </ul>
              </div>
            </div>
            
            <div className="service-image">
              <div className="image-card">
                <img src={mur_b2} alt="Peinture - mur après" className="image-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section extérieure déplacée vers la page Ravalement */}

      {/* Photos Avant/Après Section */}
      <section className="photos-section service-section-alt">
        <div className="container">
          <h2 className={`section-title scroll-animate ${photosVisible ? 'visible' : ''}`} ref={photosRef}>Nos Réalisations - Avant/Après</h2>
          <p className={`photos-subtitle scroll-animate ${photosVisible ? 'visible' : ''}`}>Découvrez la transformation de nos projets de peinture</p>
          
          <div className={`photos-grid scroll-animate ${photosVisible ? 'visible' : ''}`}>
            {photos.map((photo) => (
              <div key={photo.id} className="photo-card" onClick={() => openModal(photo)}>
                <div className="photo-container">
                  <div className="photo-before">
                    <img src={photo.beforeSrc} alt={`${photo.title} - Avant`} />
                  </div>
                  <div className="photo-after">
                    <img src={photo.afterSrc} alt={`${photo.title} - Après`} />
                  </div>
                </div>
                <div className="photo-info">
                  <h3>{photo.title}</h3>
                  <p>{photo.description}</p>
                </div>
                <div className="photo-overlay">
                  <img src={iconLoupe} alt="Zoom" className="zoom-icon" style={{height:'20px', width:'20px'}} />
                  <p>Cliquez pour agrandir</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeries retirées */}

      {/* Types de Peinture Section */}
      <section className="types-peinture-section service-section-alt">
        <div className="container">
          <h2 className="section-title">Nos Types de Peinture</h2>
          <div className="peinture-grid">
            <div className="peinture-type-card">
            <div className="service-icon"><img src={iconPeinture} alt="Peinture" style={{height:'48px', width:'48px'}} /></div>
              <h3>Peinture</h3>
              <p>Peinture des murs, plafonds, boiseries. Finitions soignées pour votre intérieur.</p>
              <ul className="peinture-features">
                <li>Matériaux écologiques</li>
                <li>Finitions variées</li>
                <li>Conseils couleurs</li>
              </ul>
            </div>

            <div className="peinture-type-card">
            <div className="couverture-icon">
                <img src={iconEtanche} alt="Étanchéité" style={{height:'48px', width:'48px'}} />
              </div>
              <h3>Peinture Extérieure</h3>
              <p>Ravalement, protection façade. Résistance aux intempéries et durabilité.</p>
              <ul className="peinture-features">
                <li>Hydrofuge</li>
                <li>Anti-UV</li>
                <li>Durabilité 10+ ans</li>
              </ul>
            </div>

            <div className="peinture-type-card">
            <div className="couverture-icon">
              <img src={iconShield} alt="Protection renforcée" style={{height:'42px', width:'42px'}} />
            </div>
              <h3>Peinture Spécialisée</h3>
              <p>Anti-humidité, anti-taches, magnétique. Solutions techniques adaptées.</p>
              <ul className="peinture-features">
                <li>Propriétés techniques</li>
                <li>Solutions adaptées</li>
                <li>Performance optimale</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <h2 className={`section-title scroll-animate ${processVisible ? 'visible' : ''}`} ref={processRef}>Notre Processus de Peinture</h2>
          <div className={`process-steps scroll-animate ${processVisible ? 'visible' : ''}`}>
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Analyse des Surfaces</h3>
              <p>Évaluation de l'état des surfaces et identification des travaux de préparation nécessaires.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Préparation</h3>
              <p>Ponçage, rebouchage, nettoyage et application des primaires pour une base parfaite.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Application</h3>
              <p>Application de la peinture avec techniques professionnelles et finitions soignées.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Finitions</h3>
              <p>Nettoyage, retouches et vérification de la qualité pour un résultat parfait.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="devis" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Besoin d'un devis pour vos travaux de peinture ?</h2>
            <p>Contactez-nous pour discuter de votre projet et obtenir une estimation gratuite.</p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      {/* Modal pour afficher les photos en grand */}
      {selectedPhoto && (
        <div className="photo-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-photo-container">
              <div className="modal-photo-before">
                <img src={selectedPhoto.beforeSrc} alt={`${selectedPhoto.title} - Avant`} />
              </div>
              <div className="modal-photo-after">
                <img src={selectedPhoto.afterSrc} alt={`${selectedPhoto.title} - Après`} />
              </div>
            </div>
            <div className="modal-info">
              <h3>{selectedPhoto.title}</h3>
              <p>{selectedPhoto.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeinturePage;
